export interface Currency {
  code: string;
  name: string;
  symbol: string;
  locale: string;
  decimals: number;
  /** Region-appropriate name for indirect tax, e.g. GST / VAT / Sales Tax. */
  taxLabel: string;
  /** Sensible starting tax rate for the region; always editable. */
  suggestedTaxRate: number;
  /** Region-appropriate label for the business tax registration number. */
  taxIdLabel: string;
}

/**
 * The catalog's base prices are authored in US Dollars, so USD is the default.
 * Every other currency remains fully supported and selectable during setup.
 *
 * There is deliberately NO conversion anywhere in the product. Selecting a
 * different currency changes the symbol, the locale grouping and the decimal
 * places — it does not touch the numbers. A business quoting in rupees is
 * expected to reprice its own catalog, which it can do from the Services page.
 * Inventing exchange rates would silently misquote real customers.
 *
 * The subscription QuotyLab charges for itself is a separate matter entirely
 * and always bills in USD — see `config/subscription.ts`. A business quoting in
 * ₹ or € still pays the same fee.
 */
export const CURRENCIES: readonly Currency[] = [
  { code: 'USD', name: 'US Dollar',          symbol: '$',   locale: 'en-US', decimals: 2, taxLabel: 'Sales Tax', suggestedTaxRate: 8.5,  taxIdLabel: 'Tax ID' },
  { code: 'EUR', name: 'Euro',               symbol: '€', locale: 'de-DE', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 20, taxIdLabel: 'VAT Number' },
  { code: 'GBP', name: 'British Pound',      symbol: '£', locale: 'en-GB', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 20, taxIdLabel: 'VAT Number' },
  // Indian pricing is quoted in whole rupees; `decimals: 0` is what keeps the
  // UI and the PDF showing ₹1,500 rather than ₹1,500.00.
  { code: 'INR', name: 'Indian Rupee',       symbol: '₹', locale: 'en-IN', decimals: 0, taxLabel: 'GST', suggestedTaxRate: 18, taxIdLabel: 'GSTIN' },
  { code: 'AUD', name: 'Australian Dollar',  symbol: 'A$',  locale: 'en-AU', decimals: 2, taxLabel: 'GST', suggestedTaxRate: 10, taxIdLabel: 'ABN' },
  { code: 'CAD', name: 'Canadian Dollar',    symbol: 'C$',  locale: 'en-CA', decimals: 2, taxLabel: 'GST/HST', suggestedTaxRate: 13, taxIdLabel: 'GST Number' },
  { code: 'AED', name: 'UAE Dirham',         symbol: 'AED', locale: 'en-AE', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 5,  taxIdLabel: 'TRN' },
  { code: 'SAR', name: 'Saudi Riyal',        symbol: 'SAR', locale: 'en-SA', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 15, taxIdLabel: 'VAT Number' },
  { code: 'SGD', name: 'Singapore Dollar',   symbol: 'S$',  locale: 'en-SG', decimals: 2, taxLabel: 'GST', suggestedTaxRate: 9,  taxIdLabel: 'GST Number' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', locale: 'en-NZ', decimals: 2, taxLabel: 'GST', suggestedTaxRate: 15, taxIdLabel: 'GST Number' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R',   locale: 'en-ZA', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 15, taxIdLabel: 'VAT Number' },
  { code: 'MYR', name: 'Malaysian Ringgit',  symbol: 'RM',  locale: 'ms-MY', decimals: 2, taxLabel: 'SST', suggestedTaxRate: 6,  taxIdLabel: 'SST Number' },
  { code: 'PHP', name: 'Philippine Peso',    symbol: '₱', locale: 'en-PH', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 12, taxIdLabel: 'TIN' },
  { code: 'NGN', name: 'Nigerian Naira',     symbol: '₦', locale: 'en-NG', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 7.5, taxIdLabel: 'TIN' },
  { code: 'KES', name: 'Kenyan Shilling',    symbol: 'KSh', locale: 'en-KE', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 16, taxIdLabel: 'PIN' },
  { code: 'PKR', name: 'Pakistani Rupee',    symbol: 'Rs',  locale: 'en-PK', decimals: 0, taxLabel: 'GST', suggestedTaxRate: 18, taxIdLabel: 'NTN' },
  { code: 'BDT', name: 'Bangladeshi Taka',   symbol: '৳', locale: 'en-BD', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 15, taxIdLabel: 'BIN' },
  { code: 'LKR', name: 'Sri Lankan Rupee',   symbol: 'Rs',  locale: 'en-LK', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 18, taxIdLabel: 'VAT Number' },
  { code: 'JPY', name: 'Japanese Yen',       symbol: '¥', locale: 'ja-JP', decimals: 0, taxLabel: 'Consumption Tax', suggestedTaxRate: 10, taxIdLabel: 'Tax ID' },
  { code: 'CHF', name: 'Swiss Franc',        symbol: 'CHF', locale: 'de-CH', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 8.1, taxIdLabel: 'VAT Number' },
  { code: 'SEK', name: 'Swedish Krona',      symbol: 'kr',  locale: 'sv-SE', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 25, taxIdLabel: 'VAT Number' },
  { code: 'NOK', name: 'Norwegian Krone',    symbol: 'kr',  locale: 'nb-NO', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 25, taxIdLabel: 'VAT Number' },
  { code: 'DKK', name: 'Danish Krone',       symbol: 'kr',  locale: 'da-DK', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 25, taxIdLabel: 'VAT Number' },
  { code: 'PLN', name: 'Polish Zloty',       symbol: 'zł', locale: 'pl-PL', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 23, taxIdLabel: 'NIP' },
  { code: 'BRL', name: 'Brazilian Real',     symbol: 'R$',  locale: 'pt-BR', decimals: 2, taxLabel: 'ISS', suggestedTaxRate: 5,  taxIdLabel: 'CNPJ' },
  { code: 'MXN', name: 'Mexican Peso',       symbol: 'MX$', locale: 'es-MX', decimals: 2, taxLabel: 'IVA', suggestedTaxRate: 16, taxIdLabel: 'RFC' },
  { code: 'IDR', name: 'Indonesian Rupiah',  symbol: 'Rp',  locale: 'id-ID', decimals: 0, taxLabel: 'PPN', suggestedTaxRate: 11, taxIdLabel: 'NPWP' },
  { code: 'THB', name: 'Thai Baht',          symbol: '฿', locale: 'th-TH', decimals: 2, taxLabel: 'VAT', suggestedTaxRate: 7, taxIdLabel: 'Tax ID' },
  { code: 'VND', name: 'Vietnamese Dong',    symbol: '₫', locale: 'vi-VN', decimals: 0, taxLabel: 'VAT', suggestedTaxRate: 8, taxIdLabel: 'Tax Code' },
  { code: 'TRY', name: 'Turkish Lira',       symbol: '₺', locale: 'tr-TR', decimals: 2, taxLabel: 'KDV', suggestedTaxRate: 20, taxIdLabel: 'Tax Number' },
] as const;

const CURRENCY_INDEX: ReadonlyMap<string, Currency> = new Map(
  CURRENCIES.map((currency) => [currency.code, currency]),
);

/**
 * The currency a new business starts with. Editable during setup and at any
 * time afterwards from Settings.
 */
export const DEFAULT_CURRENCY_CODE = 'USD';

export const FALLBACK_CURRENCY: Currency =
  CURRENCIES.find((currency) => currency.code === DEFAULT_CURRENCY_CODE) ??
  (CURRENCIES[0] as Currency);

export function findCurrency(code: string | undefined): Currency {
  if (!code) return FALLBACK_CURRENCY;
  return CURRENCY_INDEX.get(code) ?? FALLBACK_CURRENCY;
}
